import { AmpProduct, useTrackAmpClicked } from '@/utils/amplitude/analytics';
import { AUTOMATED_CAROUSEL_CARD_OFFSET_MODIFIER } from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/AutomatedCatalogCarouselCard.types';
import { AutomatedCarousel } from '@liveauctioneers/hammer-ui-core/automatedCarousel';
import { Carousel } from '@liveauctioneers/hammer-ui-core/carousel';
import { ComponentProps, useMemo } from 'react';
import { getFeaturedCatalogsUrl } from '@/utils/urls';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { useIsMobileLayout } from '@/hooks/useIsMobileLayout';
import AutomatedCatalogCarouselCard from '@/components/FeaturedCatalogCarousel/components/AutomatedCatalogCarouselCard/AutomatedCatalogCarouselCard';
import CarouselLoadingWrapper from '@/components/CarouselLoadingWrapper/CarouselLoadingWrapper';
import useGetFeaturedCatalogIds from './hooks/useGetFeaturedCatalogIds';

type Props = {
    width: number;
};

const FeaturedCatalogCarousel = ({ width }: Props) => {
    const isMobile = useIsMobileLayout();
    const { formatMessage } = useIntl();

    const catalogIds = useGetFeaturedCatalogIds();
    const trackAmpClicked = useTrackAmpClicked();

    const carouselCards = useMemo(() => {
        return catalogIds.map((catalogId) => ({
            card: (
                <AutomatedCatalogCarouselCard
                    catalogId={catalogId}
                    width={width}
                />
            ),
            key: `${catalogId}-featured`,
        }));
    }, [catalogIds, width]);

    const title = useMemo(() => formatMessage({ id: 'featured_auctions' }), [formatMessage]);
    const action: ComponentProps<typeof Carousel>['action'] = useMemo(
        () => ({
            onClick: () => {
                trackAmpClicked({
                    ampProduct: AmpProduct.FeaturedAuctions,
                    catalogId: null,
                    houseId: null,
                });
            },
            path: getFeaturedCatalogsUrl(),
            text: formatMessage({
                id: 'viewAll',
            }),
        }),
        [formatMessage, trackAmpClicked]
    );

    if (!Boolean(carouselCards?.length)) {
        return null;
    }

    return (
        <CarouselLoadingWrapper id="featuredCatalog">
            <AutomatedCarousel
                action={action}
                cardOffset={isMobile ? width / AUTOMATED_CAROUSEL_CARD_OFFSET_MODIFIER : 0}
                cards={carouselCards}
                // Probably "- 2" from the gaps and how they interact with page calculation in the carousel.¯\_(ツ)_/¯
                expectedPageCount={carouselCards.length - 2}
                fullPage
                progressBarGap={isMobile ? undefined : 'sm'}
                title={title}
                useOverlayControls
            />
        </CarouselLoadingWrapper>
    );
};

export default FeaturedCatalogCarousel;
